import { AppHelper } from '../appHelper/appHelper';
import { RequestCacheService } from './request-cache-service';
import { CacheUriRegistrationService } from './cache-uri-registration-service';
import { Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import { Observable, of as _of } from "rxjs";
import { map, share, filter } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
// inspired mostly from https://medium.com/@vikeshm/data-caching-angular-http-interceptor-2d87f95e2340

  constructor(
    private cacheUriRegistrationService: CacheUriRegistrationService,
    private cache: RequestCacheService,
    private router: Router,
  ) {
  }

  public intercept(httpRequest: HttpRequest<any>, handler: HttpHandler): Observable<HttpEvent<any>> {
    // Check if caching is disabled (AppHelper.defaultCacheTimeoutSeconds = 0)
    if (!AppHelper.defaultCacheTimeoutSeconds) {
      return handler.handle(httpRequest);
    }

    let isGet = httpRequest.method === "GET";
    let isExcluded = this.cacheUriRegistrationService.checkExcluded(httpRequest.url);
    let ttl = this.cacheUriRegistrationService.checkIncluded(httpRequest.url);

    // A 'client-cache' HTTP header can be used to force caching status
    // Possible values:
    // - 'on': cacheable with default timeout
    // - <timeout in seconds>: number of seconds for cache timeout (as a string)
    // - 'off': not cacheable
    if (!ttl) {
      let val = httpRequest.headers.get("client-cache");
      if (val) {
        if (val === 'on') { 
          ttl = AppHelper.defaultCacheTimeoutSeconds; 
        } else if (val === 'off') {
          isExcluded = true;
          ttl = 0;
        } else {
          ttl = +val;
        }
      }
    }

    // Cache only when:
    // 1. It's a GET request (and is not excluded)
    // 2. If URI is included in cacheUriRegistrationService
    if (isExcluded || !(isGet || ttl)) { 
      return handler.handle(httpRequest);
    }

    // Build the cache key
    let cacheKey = httpRequest.urlWithParams;
    if (!isGet && httpRequest.body) {
      cacheKey += ':' + httpRequest.serializeBody();
    }

    // Checked if there is cached data for this URI
    const lastResponse = this.cache.get(cacheKey);
    if (lastResponse) {
      // In case of parallel requests to same URI,
      // return the request already in progress
      // otherwise return the last cached data
      return (lastResponse instanceof Observable)
        ? lastResponse : _of(lastResponse.clone());
    }

    // If the request of going through for first time
    // then let the request proceed and cache the response
    const requestHandle = handler.handle(httpRequest).pipe(
      filter(event => event instanceof HttpResponse),
      map((event: HttpResponse<any>) => {
        this.cache.set(cacheKey, event.clone(), ttl);
        return event;
      }),
      share()
    );

    // Meanwhile cache the request Observable to handle parallel request
    this.cache.set(cacheKey, requestHandle, ttl);

    return requestHandle;
  }
}
